import { Button, Callout, Divider, Icon, Intent, NonIdealState, PopoverPosition, Tooltip } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import * as React from 'react'
import Client from '../../../../client'
import { ListingStrategyLink } from '../../../../components/links/ListingStrategyLink'
import useAPI from '../../../../hooks/useAPI'
import { usePermissions } from '../../../../hooks/usePermissions'
import { ListingStrategyRun } from '../../../../types/api'
import { ListingProperty } from '../ListingHistorySection/components/ListingProperty'
import { ChangeStrategyDialog } from './components/ChangeStrategyDialog'
import { RunProgressIndicator } from './components/RunProgressIndicator'
import { resolveRunState } from './utils'

const styles = {
  spinnerLine: {
    display: 'flex',
    alighItems: 'center',
    marginBottom: 10
  },
  strategyName: {
    margin: 0,
    marginLeft: 7
  },
  titleButton: {
    justifyContent: 'space-between'
  },
  noProgressCallout: {
    marginBottom: 15
  },
  noStrategyBox: {
    width: '50%',
    margin: '0 auto'
  },
  concurrentStepHelper: {
    marginTop: 16
  }
}

interface Props {
  run?: ListingStrategyRun
  productId: number
  marketAccountIds?: number[]
  concurrent?: boolean
  reload: (setLoadingIndicator?: boolean) => void
  disabledActions?: boolean
  disabledActionsMessage?: string
  isMedia?: boolean
  isRepricingPaused?: boolean
}

export const StrategySection: React.FC<Props> = props => {
  const humanReadableRunState = resolveRunState(props.run)
  const isRunning = props.run?.running
  const isStarted = props.run?.currentStep !== null
  const { reload } = props
  const { permissions } = usePermissions()

  const humanReadableUnpaidAction = (() => {
    switch (props.run?.unpaidStepAction) {
      case 'RERUN_WITH_CURRENT_PRICE':
        return 'Relist at current price'
      case 'RERUN_WITH_STARTING_PRICE':
        return 'Relist at start price'
      case null:
        return 'Move to next step'
      default:
        return null
    }
  })()

  const isFinished =
    props.run?.currentStep && props.run?.currentStep.rank + 1 === props.run?.totalSteps && !props.run?.running

  const [changeStrategyOpen, setChangeStrategyOpen] = React.useState(false)

  const [onPauseRunResponse, onPauseRun] = useAPI(() =>
    Client.updateListingStrategyRun(props.run.id, { listingStrategyRun: { running: false } })
  )

  const [onResumeRunResponse, onResumeRun] = useAPI(() =>
    Client.updateListingStrategyRun(props.run.id, { listingStrategyRun: { running: true } })
  )

  const [onRestartRunResponse, onRestartRun] = useAPI(() =>
    Client.updateListingStrategyRun(props.run.id, {
      listingStrategyRun: { running: true, listingStrategyStepId: null }
    })
  )

  const [onPauseRepricingResponse, onPauseRepricing] = useAPI(() =>
    Client.updateRepricing(props.productId, {
      product: {
        is_repricing_paused: true
      }
    })
  )

  const [onResumeRepricingResponse, onResumeRepricing] = useAPI(() =>
    Client.updateRepricing(props.productId, {
      product: {
        is_repricing_paused: false
      }
    })
  )

  React.useEffect(() => {
    if (
      onResumeRunResponse.data ||
      onPauseRunResponse.data ||
      onRestartRunResponse.data ||
      onPauseRepricingResponse.data ||
      onResumeRepricingResponse.data
    ) {
      reload(false)
    }
  }, [
    onResumeRunResponse.data,
    onPauseRunResponse.data,
    onRestartRunResponse.data,
    onPauseRepricingResponse.data,
    onResumeRepricingResponse.data,
    reload
  ])

  return (
    <div>
      {changeStrategyOpen && (
        <ChangeStrategyDialog
          currentStrategyId={props.run?.listingStrategyId}
          productId={props.productId}
          marketAccountIds={props.marketAccountIds}
          onClose={() => setChangeStrategyOpen(false)}
          onChangeSuccess={() => {
            setChangeStrategyOpen(false)
            props.reload()
            analytics.track('Listing Strategy Changed')
          }}
          canEscapeKeyClose
          isOpen
        />
      )}
      <div className="drawer-header" style={styles.titleButton}>
        Strategy
        {permissions.includes('listings.manage') && (
          <Tooltip content={props.disabledActionsMessage} disabled={!props.disabledActions}>
            <Button
              minimal
              intent={Intent.PRIMARY}
              text={props.run ? 'Use another strategy' : 'Assign a strategy'}
              onClick={() => setChangeStrategyOpen(true)}
              disabled={props.disabledActions}
            />
          </Tooltip>
        )}
      </div>
      {props.isMedia && (
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
          <Callout intent={Intent.PRIMARY} style={{ flex: 1 }}>
            Controlled by Repricing
          </Callout>
          {permissions.includes('listings.manage') && (
            <div style={{ marginLeft: 10 }}>
              {props.isRepricingPaused ? (
                <Button
                  intent={Intent.SUCCESS}
                  text="Resume Repricing"
                  onClick={() => {
                    onResumeRepricing()
                    analytics.track('Repricing Resumed')
                  }}
                />
              ) : (
                <Button
                  intent={Intent.WARNING}
                  text="Pause Repricing"
                  onClick={() => {
                    onPauseRepricing()
                    analytics.track('Repricing Paused')
                  }}
                />
              )}
            </div>
          )}
        </div>
      )}
      {props.run && !props.run?.canProgress && (
        <Callout intent={Intent.WARNING} title="Strategy will not continue" style={styles.noProgressCallout}>
          <p>
            Current listings must be expired to run a strategy. See the &ldquo;Listings&rdquo; section for more
            information.
          </p>
        </Callout>
      )}
      {!props.run && (
        <div style={styles.noStrategyBox}>
          <NonIdealState title="No strategy assigned" icon={<Icon icon={IconNames.GIT_NEW_BRANCH} iconSize={30} />}>
            <p>This product is controlled manually. Assign a strategy with the &ldquo;Actions&rdquo; menu above</p>
          </NonIdealState>
        </div>
      )}
      {props.run && (
        <>
          <div>
            <div style={styles.spinnerLine}>
              <Tooltip
                content={
                  props.run.currentStep
                    ? `Step ${props.run.currentStep.rank + 1} of ${props.run.totalSteps}`
                    : 'Strategy will start from Step 1'
                }
                disabled={!props.run.currentStep}
                position={PopoverPosition.BOTTOM_LEFT}
              >
                <RunProgressIndicator
                  currentRank={props.run.currentStep?.rank}
                  totalSteps={props.run.totalSteps}
                  running={props.run.running}
                />
              </Tooltip>
              <p style={styles.strategyName}>
                {humanReadableRunState}{' '}
                <ListingStrategyLink strategyId={props.run.listingStrategyId} text={props.run.name} />
              </p>
            </div>
            <div style={{ display: 'flex' }}>
              <ListingProperty name="Auto-recycle" value={props.run.autoPurge ? 'On' : 'Off'} />
              <ListingProperty name="Unpaid auction handling" value={humanReadableUnpaidAction} />
            </div>
            {permissions.includes('listings.manage') && (
              <>
                <Divider style={{ margin: 0, marginTop: 10, padding: 0 }} />
                <div style={{ marginTop: 10 }}>
                  {isRunning && (
                    <Tooltip content={props.disabledActionsMessage} disabled={!props.disabledActions}>
                      <Button
                        text="Pause auto-relisting"
                        loading={onPauseRunResponse.loading}
                        onClick={() => {
                          onPauseRun()
                          analytics.track('Listing Strategy Paused')
                        }}
                        disabled={props.disabledActions}
                      />
                    </Tooltip>
                  )}
                  {!isRunning && !isFinished && (
                    <Tooltip content={props.disabledActionsMessage} disabled={!props.disabledActions}>
                      <Button
                        text="Resume strategy"
                        loading={onResumeRunResponse.loading}
                        onClick={() => {
                          onResumeRun()
                          analytics.track('Listing Strategy Resumed')
                        }}
                        disabled={props.disabledActions}
                      />
                    </Tooltip>
                  )}
                  {isStarted && (isRunning || isFinished) && (
                    <Tooltip content={props.disabledActionsMessage} disabled={!props.disabledActions}>
                      <Button
                        text={isFinished ? 'Restart now' : 'Restart after current listing'}
                        loading={onRestartRunResponse.loading}
                        onClick={() => {
                          onRestartRun()
                          analytics.track('Listing Strategy Restarted')
                        }}
                        disabled={props.disabledActions}
                      />
                    </Tooltip>
                  )}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  )
}
